/**
 * Amazon Ad Tag Event Tracking
 * Handles various user interactions for Amazon DSP remarketing
 */

class AmazonAdTracking {
  constructor() {
    this.init();
  }

  init() {
    // Track page views
    this.trackPageView();
    
    // Track product interactions
    this.trackProductViews();
    this.trackAddToCart();
    this.trackPurchase();
    
    // Track collection views
    this.trackCollectionViews();
    
    // Track search
    this.trackSearch();
  }

  /**
   * Helper to log and send Amazon events
   */
  sendAmazonEvent(eventName, attributes) {
    // Remove keys with null, undefined, or empty string values
    if (attributes && typeof attributes === 'object') {
      attributes = Object.fromEntries(
        Object.entries(attributes).filter(
          ([, v]) => v !== undefined && v !== null && v !== ''
        )
      );
    }
    if (window.amazonTrackingDebug || true) {
      if (attributes) {
        console.log(`[AmazonAdTracking] Event: ${eventName}`, attributes);
      } else {
        console.log(`[AmazonAdTracking] Event: ${eventName}`);
      }
    }
    if (typeof amzn !== 'undefined') {
      if (attributes) {
        amzn("trackEvent", eventName, attributes);
      } else {
        amzn("trackEvent", eventName);
      }
    }
  }

  /**
   * Track page view event
   */
  trackPageView() {
    this.sendAmazonEvent("PageView");
  }

  /**
   * Track product view events
   */
  trackProductViews() {
    // Only fire on product pages (Shopify convention: /products/ in URL)
    if (window.location.pathname.includes('/products/')) {
      const productId = document.querySelector('[data-product-id]')?.getAttribute('data-product-id');
      const productTitle = document.querySelector('h1')?.textContent?.trim();
      const productPrice = this.getProductPrice();
      const productVariant = this.getSelectedVariant();
      if (productId) {
        this.sendAmazonEvent("ProductView", {
          productId: productId,
          productTitle: productTitle || '',
          value: productPrice,
          currencyCode: window.Shopify?.currency?.active || 'USD',
          variantId: productVariant?.id || '',
          variantTitle: productVariant?.title || '',
          clientDedupeId: `product_view_${productId}_${Date.now()}`
        });
      }
    }
  }

  /**
   * Track add to cart events
   */
  trackAddToCart() {
    // Listen for form submissions (add to cart)
    document.addEventListener('submit', (event) => {
      if (event.target.closest('form[data-type="add-to-cart-form"]')) {
       
        const form = event.target.closest('form[data-type="add-to-cart-form"]');
        const productId = form.getAttribute('data-product-id');
        const quantity = parseInt(form.querySelector('input[name="quantity"]')?.value || '1', 10);
        const variantId = form.querySelector('input[name="id"]')?.value;
        const productTitle = document.querySelector('h1')?.textContent?.trim();
        const productPrice = this.getProductPrice();
        const variant = this.getSelectedVariant();
        // Ensure value is a number and not empty
        const value = Number(productPrice) * quantity;
        if (productId) {
          this.sendAmazonEvent("AddToCart", {
            productId: productId,
            productTitle: productTitle || '',
            value: value,
            currencyCode: window.Shopify?.currency?.active || 'USD',
            quantity: quantity,
            variantId: variantId || variant?.id || '',
            variantTitle: variant?.title || '',
            clientDedupeId: `add_to_cart_${productId}_${Date.now()}`
          });
        }
      }
    });
    // Also listen for cart drawer add to cart
    document.addEventListener('cart:item:added', (event) => {
      const item = event.detail?.item;
      if (item) {
        // Ensure value is a number and not empty
        const value = Number(item.final_price) * item.quantity;
        this.sendAmazonEvent("AddToCart", {
          productId: item.product_id?.toString(),
          productTitle: item.product_title || '',
          value: value,
          currencyCode: window.Shopify?.currency?.active || 'USD',
          quantity: item.quantity,
          variantId: item.variant_id?.toString(),
          variantTitle: item.variant_title || '',
          category: this.getProductCategory(),
          brand: this.getProductBrand(),
          clientDedupeId: `add_to_cart_${item.product_id}_${Date.now()}`
        });
      }
    });
  }

  /**
   * Track purchase events
   */
  trackPurchase() {
    // Check if we're on a thank you page (order confirmation)
    if (window.location.pathname.includes('/orders/') && window.location.search.includes('key=')) {
      // Get order details from the page
      const orderId = this.getOrderId();
      const orderTotal = this.getOrderTotal();
      const orderItems = this.getOrderItems();
      
      if (orderId) {
        this.sendAmazonEvent("Purchase", {
          orderId: orderId,
          value: orderTotal,
          currencyCode: window.Shopify?.currency?.active || 'USD',
          items: orderItems,
          clientDedupeId: `purchase_${orderId}`
        });
      }
    }

    // Also track checkout button clicks
    document.addEventListener('click', (event) => {
      if (event.target.closest('button[name="checkout"]') || 
          event.target.closest('a[href*="/checkout"]')) {
        const cartTotal = this.getCartTotal();
        const cartItems = this.getCartItems();
        
        this.sendAmazonEvent("InitiateCheckout", {
          value: cartTotal,
          currencyCode: window.Shopify?.currency?.active || 'USD',
          items: cartItems,
          clientDedupeId: `checkout_${Date.now()}`
        });
      }
    });
  }

  /**
   * Track collection view events
   */
  trackCollectionViews() {
    if (window.location.pathname.includes('/collections/')) {
      // Get the collection title from the slideshow title
      const collectionTitle = document.querySelector('.slideshow__title')?.textContent?.trim() || '';
      if (collectionTitle) {
        this.sendAmazonEvent("CollectionView", {
          collectionTitle: collectionTitle,
          clientDedupeId: `collection_view_${collectionTitle}_${Date.now()}`
        });
      }
    }
  }

  /**
   * Track search events
   */
  trackSearch() {
    // Listen for search form submissions
    document.addEventListener('submit', (event) => {
      if (event.target.closest('form[action*="/search"]') || 
          event.target.closest('form[action*="search"]')) {
        const searchInput = event.target.querySelector('input[name="q"]') || 
                           event.target.querySelector('input[type="search"]');
        const searchTerm = searchInput?.value?.trim();
        
        if (searchTerm) {
          this.sendAmazonEvent("Search", {
            searchTerm: searchTerm,
            clientDedupeId: `search_${searchTerm}_${Date.now()}`
          });
        }
      }
    });
  }

  /**
   * Helper methods
   */
  getProductPrice() {
    // Use the price selector from the provided image
    const priceElement = document.querySelector('span.f-price-item.f-price-item--regular');
    if (priceElement) {
      // Trim whitespace first, then remove any non-numeric characters except the decimal point
      const priceText = priceElement.textContent.trim().replace(/[^0-9.]/g, '');
      return parseFloat(priceText) || 0;
    }
    return 0;
  }

  getSelectedVariant() {
    const variantSelect = document.querySelector('select[name="id"]') || 
                         document.querySelector('input[name="id"]');
    if (variantSelect) {
      const selectedOption = variantSelect.querySelector('option[selected]') || 
                           variantSelect.querySelector('option[value="' + variantSelect.value + '"]');
      if (selectedOption) {
        return {
          id: selectedOption.value,
          title: selectedOption.textContent.trim()
        };
      }
    }
    return null;
  }


  getOrderId() {
    const orderIdElement = document.querySelector('[data-order-id]') ||
                          document.querySelector('.order-id') ||
                          document.querySelector('[data-order-number]');
    return orderIdElement?.textContent?.trim() || 
           orderIdElement?.getAttribute('data-order-id') ||
           orderIdElement?.getAttribute('data-order-number') || '';
  }

  getOrderTotal() {
    const totalElement = document.querySelector('[data-order-total]') ||
                        document.querySelector('.order-total') ||
                        document.querySelector('.total-price');
    
    if (totalElement) {
      const totalText = totalElement.textContent.replace(/[^0-9.]/g, '');
      return parseFloat(totalText) || 0;
    }
    return 0;
  }

  getOrderItems() {
    const items = [];
    const itemElements = document.querySelectorAll('[data-order-item]') ||
                        document.querySelectorAll('.order-item');
    
    itemElements.forEach(item => {
      const productId = item.getAttribute('data-product-id') ||
                       item.querySelector('[data-product-id]')?.getAttribute('data-product-id');
      const quantity = item.querySelector('[data-quantity]')?.textContent?.trim() || '1';
      const price = item.querySelector('[data-price]')?.textContent?.replace(/[^0-9.]/g, '') || '0';
      
      if (productId) {
        items.push({
          productId: productId,
          quantity: parseInt(quantity),
          price: parseFloat(price)
        });
      }
    });
    
    return items;
  }

  getCartTotal() {
    const cartTotalElement = document.querySelector('[data-cart-total]') ||
                            document.querySelector('.cart-total') ||
                            document.querySelector('.totals__subtotal-value');
    
    if (cartTotalElement) {
      const totalText = cartTotalElement.textContent.replace(/[^0-9.]/g, '');
      return parseFloat(totalText) || 0;
    }
    return 0;
  }

  getCartItems() {
    const items = [];
    const cartItems = document.querySelectorAll('[data-cart-item]') ||
                     document.querySelectorAll('.cart-item');
    
    cartItems.forEach(item => {
      const productId = item.getAttribute('data-product-id') ||
                       item.querySelector('[data-product-id]')?.getAttribute('data-product-id');
      const quantity = item.querySelector('[data-quantity]')?.textContent?.trim() || '1';
      const price = item.querySelector('[data-price]')?.textContent?.replace(/[^0-9.]/g, '') || '0';
      
      if (productId) {
        items.push({
          productId: productId,
          quantity: parseInt(quantity),
          price: parseFloat(price)
        });
      }
    });
    
    return items;
  }
}

// Initialize Amazon Ad Tracking when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new AmazonAdTracking();
});

// Also initialize on page transitions (for SPA-like behavior)
document.addEventListener('shopify:section:load', () => {
  new AmazonAdTracking();
}); 